import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { MoveType } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { IBaseActionsInjectable, IBaseShortcutActionsServices } from '../interface';
import { TabActionService } from '@src/slide/application/service/shortcut-key/core/base/tab';
import { RegionClassName } from '@src/js/constant';

export default class BaseShortcutActions {
    protected services: IBaseShortcutActionsServices;
    // 异化类，异化逻辑在 injectable 中实现
    protected injectable?: IBaseActionsInjectable;
    private elementMoveTimer: ReturnType<typeof setTimeout> | null = null;
    private mouseWheelTimer: ReturnType<typeof setTimeout> | null = null;
    private lastTimeStamp: number;
    private outerEditorTimeStamp: number;
    private lastHandledTimestamp: number;
    private lastDeltaY: number;
    private tabActionService: TabActionService;

    public constructor(
        services: Partial<IBaseShortcutActionsServices>,
        injectable?: IBaseActionsInjectable,
    ) {
        this.injectable = injectable;
        this.setServicesDelegates(services);
        this.tabActionService = new TabActionService();
        this.lastTimeStamp = 0;
    }

    public setServicesDelegates(services: Partial<IBaseShortcutActionsServices>) {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    // 目前快捷键好像没配这几个
    public forward() {
        this.selectionService?.currentSlide?.moveElement(1);
    }
    public backward() {
        this.selectionService?.currentSlide?.moveElement(-1);
    }
    public front() {
        this.selectionService?.currentSlide?.setCurrentElementToFrontOrBack(MoveType.FRONT);
    }
    public back() {
        this.selectionService?.currentSlide?.setCurrentElementToFrontOrBack(MoveType.BACK);
    }
    public async copy() {
        const copyPasteManager = await this.copyPasteManager?.promise();
        copyPasteManager?.triggerCopyEvent();
    }
    public async paste() {
        const copyPasteManager = await this.copyPasteManager?.promise();
        copyPasteManager?.triggerPasteEvent();
    }
    public async cut() {
        const copyPasteManager = await this.copyPasteManager?.promise();
        copyPasteManager?.triggerCutEvent();
    }
    public async ctrl() {
        const copyPasteManager = await this.copyPasteManager?.promise();
        copyPasteManager?.keyEventCtrl();
    }

    public selectAll(event: KeyboardEvent) {
        if (this.checkTextEditing()) return;

        // 阻止系统的selectAll事件，否则会选中全部userSelect为非none的元素
        event.preventDefault();
        event.stopPropagation();

        let handled = false;
        switch (this.selectionService?.lastMouseDownRegion) {
            case RegionType.SLIDELIST:
                this.uiDataService?.presentation.selectAll();
                break;
            case RegionType.EDITOR:
                if (this.selectionService?.selectedElements.length === 1) {
                    const element = this.selectionService?.selectedElements?.selectedArray?.[0];
                    // @ts-ignore
                    if (element.selectAll) {
                        // @ts-ignore
                        handled = element.selectAll();
                    }
                }
                if (!handled) {
                    this.selectionService?.currentSlide?.selectAll();
                }
                break;
        }
    }
    public play() {
        if (this.checkTextEditing()) return;
        const playAction = this.playManager?.playAction;
        if (playAction) {
            playAction();

            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PC_LOCAL_PLAY,
                action: TDWActionType.HOT_KEY,
            });
        }
    }

    public up(event: KeyboardEvent) {
        if (this.checkIsInAnyEditingState()) {
            return;
        }
        if (this.checkAndMoveSelectedElements(event)) {
            return;
        }
        this.pageup(event);
    }

    public down(event: KeyboardEvent) {
        if (this.checkIsInAnyEditingState()) {
            return;
        }
        if (this.checkAndMoveSelectedElements(event)) {
            return;
        }
        this.pagedown(event);
    }

    public left(event: KeyboardEvent) {
        if (this.checkIsInAnyEditingState()) {
            return;
        }
        if (this.checkAndMoveSelectedElements(event)) {
            return;
        }

        this.pageup(event);
    }

    public right(event: KeyboardEvent) {
        if (this.checkIsInAnyEditingState()) {
            return;
        }
        if (this.checkAndMoveSelectedElements(event)) {
            return;
        }

        this.pagedown(event);
    }

    public pageup(event: KeyboardEvent) {
        if (this.checkIsInAnyEditingState()) {
            return;
        }
        if (!this.canChangeSelectedSlide()) {
            return;
        }

        this.normalPageUp(event);
    }

    public pagedown(event: KeyboardEvent) {
        if (this.checkIsInAnyEditingState()) {
            return;
        }
        if (!this.canChangeSelectedSlide()) {
            return;
        }

        this.normalPageDown(event);
    }

    public home(event: KeyboardEvent) {
        if (this.checkIsInAnyEditingState()) {
            return;
        }
        if (!this.canChangeSelectedSlide()) {
            return;
        }

        this.changeSelectedSlide(event, 0);
    }

    public end(event: KeyboardEvent) {
        if (!this.selectionService) {
            return;
        }
        if (this.checkIsInAnyEditingState()) {
            return;
        }
        if (!this.canChangeSelectedSlide()) {
            return;
        }
        this.changeSelectedSlide(event, this.selectionService.slides.length - 1);
    }

    public newSlideEnter(event: KeyboardEvent) {
        if (this.selectionService?.lastMouseDownRegion !== RegionType.SLIDELIST) {
            return;
        }
        if (this.checkIsInAnyEditingState()) {
            return;
        }
        this.addSlide(event);
    }
    public newSlideM(event: KeyboardEvent) {
        if (this.selectionService?.lastMouseDownRegion !== RegionType.SLIDELIST) {
            return;
        }
        if (this.checkIsInAnyEditingState()) {
            return;
        }
        this.addSlide(event);
    }

    public addGroup(event: KeyboardEvent) {
        if (this.selectionService?.lastMouseDownRegion !== RegionType.EDITOR) {
            return;
        }
        const elements = this.selectionService?.selectedElements.selectedArray || [];
        if (this.selectionService?.currentSlide && elements.length) {
            this.selectionService?.currentSlide?.groupElements(elements);
            event.preventDefault();
            event.stopPropagation();
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PPT_PC_EDIT_COMBINATION,
                action: TDWActionType.COMBINATION_TOTAL_UV,
            });
        }
    }

    public removeGroup(event: KeyboardEvent) {
        if (this.selectionService?.lastMouseDownRegion !== RegionType.EDITOR) {
            return;
        }
        const elements = this.selectionService?.selectedElements.selectedArray || [];
        if (this.selectionService?.currentSlide && elements.length) {
            this.selectionService?.currentSlide?.unGroupElements(elements);
            event.preventDefault();
            event.stopPropagation();
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PPT_PC_EDIT_COMBINATION,
                action: TDWActionType.REMOVE_COMBINATION_TOTAL_UV,
            });
        }
    }

    public delete(event: KeyboardEvent) {
        if (this.checkTextEditing()) {
            return;
        }
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        this.deleteInSlideOrEditorRegion();
    }

    public mousewheel(event: MouseEvent) {
        // 判断当前状态下是否需要处理滚轮事件
        if (!this.needHandleMouseWheelByCurrentState()) {
            return;
        }

        if (this.isUserListShow()) {  // 这里检查用户列表是否正在展示
            return;
        }

        const canvasObj = document.getElementById('canvas');
        if (canvasObj) {
            // 部分场景无需触发切换
            if (this.ignoreWheel(canvasObj, event)) {
                return;
            }
            // if (canvasObj.clientHeight < canvas.scrollHeight) {
            //     return;   //当内容大于canvas的话，则不响应滚轮翻页
            // }

            const x = event.pageX;
            const y = event.pageY;
            if (this.isPointInCanvasReact(x, y)) {
                if (this.hasFloatContent()) {
                    this.outerEditorTimeStamp = event.timeStamp;
                    return;
                }

                if (this.isPointInRightBarReact(x, y)) {
                    this.outerEditorTimeStamp = event.timeStamp;
                    return;
                }
                // 是否处理
                if (!this.needHandleMouseWheelByTimestamp(event)) {
                    return;
                }
                // 处理滚轮事件
                this.handleMouseWheel(event);
            } else {
                this.outerEditorTimeStamp = event.timeStamp;
            }
        }
    }

    public tabAction(event: KeyboardEvent) {
        if (!this.selectionService) {
            return;
        }
        const { selectedElements } = this.selectionService;
        const selectedSlideElements = selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE);
        // tab切换元素功能入口
        if (this.editorInstanceService?.checkEditorIsEditing()) {
            if (selectedSlideElements.length === 1 && selectedSlideElements[0].type === UIModelType.TABLE) {
                // @ts-ignore
                (selectedSlideElements[0] as ITableModel)?.hotTable?.handleTableKeyDown(e);
            }
            return;
        }
        this.tabActionService.tabSelectedModelChange(this.selectionService.currentSlide!, selectedElements);
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    /**
     * 编辑区域上面是否有浮层，有浮层的时候返回true
     */
    public hasFloatContent() {
        const point = this.injectable?.floatContentOffset ?? { x: 0, y: 0 };

        let element = document.elementFromPoint(point.x, point.y);
        while (element && element !== document.body) {
            if (element.id === RegionClassName.WORKBENCH) {
                return false;
            }
            element = element.parentElement;
        }

        return true;
    }

    protected addSlide(event: KeyboardEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.uiDataService?.presentation.addDefaultSlide();
    }


    protected changeSelectedSlide(event: KeyboardEvent, slideIndex: number) {
        if (this.editingStateManager) {
            this.editingStateManager.keyFlag4Moving = event.keyCode;
        }
        const { selectionService } = this;
        if (!selectionService) {
            return;
        }
        if (slideIndex < 0) {
            return;
        }
        if (slideIndex > selectionService.slides.length - 1) {
            return;
        }
        event.stopPropagation();
        event.preventDefault();
        const { selectedSlides, slides } = selectionService;
        selectedSlides.clear();
        selectionService.changeCurrentSlideIndexByUserAction(slideIndex);
        selectedSlides.add(slides[slideIndex]);
        selectionService.needClearSelectedSlideClearanceFlag += 1;
    }

    protected normalPageUp(event: KeyboardEvent) {
        if (!this.selectionService) {
            return;
        }
        this.changeSelectedSlide(event, this.selectionService.currentSlideIndex - 1);
    }

    protected normalPageDown(event: KeyboardEvent) {
        if (!this.selectionService) {
            return;
        }
        this.changeSelectedSlide(event, this.selectionService.currentSlideIndex + 1);
    }

    protected checkTextEditing(): boolean {
        return !!(this.editorInstanceService?.checkEditorIsEditing?.() || this.editingStateManager?.isEditing);
    }

    protected deleteInSlideOrEditorRegion() {
        switch (this.selectionService?.lastMouseDownRegion) {
            case RegionType.SLIDELIST: {
                this.deleteSlide();
                break;
            }
            case RegionType.EDITOR:
                this.deleteInEditorRegion();
                break;
        }
    }

    protected  get selectionService() {
        return this.services.selectionService;
    }

    protected get uiDataService() {
        return this.services.uiDataService;
    }

    protected get editorInstanceService() {
        return this.services.editorInstanceService;
    }

    protected get menuManager() {
        return this.services.menuManager;
    }


    protected get editingStateManager() {
        return this.services.editingStateManager;
    }

    protected get userPriviledgeService() {
        return this.services.userPriviledgeService;
    }
    protected get moveElementManager() {
        return this.services.moveElementManager;
    }

    protected get copyPasteManager() {
        return this.services.copyPasteManager;
    }

    protected get playManager() {
        return this.services.playManager;
    }

    protected getToolbarStateManager() {
        return this.services.toolbarStateManager;
    }

    protected checkIsInAnyEditingState() {
        return this.checkTextEditing() || this.editingStateManager?.isInAnyEditingState();
    }

    protected canChangeSelectedSlide() {
        return !this.menuManager?.isMenuShown
        && !this.getToolbarStateManager()?.isToolbarMenuShown;
    }

    /**
     * 使用键盘移动选中元素
     * @param event
     * @returns {boolean} true 表示已经处理完，不需要进行下一步操作
     */
    private checkAndMoveSelectedElements(event: KeyboardEvent): boolean {
        if (this.moveElementManager?.checkAndMoveSelectedElementsWithArrow(event.keyCode)) {
            event.preventDefault();
            event.stopPropagation();
            this.updateElementMoveFlag();
            return true;
        }
        return false;
    }

    private updateElementMoveFlag() {
        if (this.elementMoveTimer) {
            clearTimeout(this.elementMoveTimer);
        }
        this.editingStateManager && (this.editingStateManager.isMovingWithKeyUpDown = true);
        this.elementMoveTimer = setTimeout(() => {
            this.editingStateManager && (this.editingStateManager.isMovingWithKeyUpDown = false);
        }, 400);
    }

    private isPointInCanvasReact = (x, y) => {
        const canvasObj = document.getElementById('canvas');
        if (!canvasObj) {
            return false;
        }
        const rect = canvasObj.getBoundingClientRect();
        const y1 = rect.top;                        // canvasObj上面两个的点的y值
        const y2 = y1 + rect.height;                // canvasObj下面两个点的y值
        const x1 = rect.left;                       // canvasObj左边两个的点的x值
        const x2 = x1 + rect.width;                 // canvasObj右边两个点的x的值

        return !(x < x1 || x > x2 || y < y1 || y > y2);
    };

    private isPointInRightBarReact = (x, y) => {
        let pointInRect = false;
        const rsBarContainer = document.getElementById('rs-bar-container');
        if (rsBarContainer && rsBarContainer.getAttribute('data-display') === 'true') {
            const rect1 = rsBarContainer.getBoundingClientRect();
            const y3 = rect1.top;                        // rsBarContainer上面两个的点的y值
            const y4 = y3 + rect1.height;                // rsBarContainer下面两个点的y值
            const x3 = rect1.left;                       // rsBarContainer左边两个的点的x值
            const x4 = x3 + rect1.width;                 // rsBarContainer右边两个点的x的值
            if (!(x < x3 || x > x4 || y < y3 || y > y4)) {
                pointInRect = true;
            }
        }
        return pointInRect;
    };

    /**
     * 判断用户列表是否弹出
     */
    private isUserListShow = () => {
        const userList = document.getElementsByClassName('otheruserstable-wrapper open');
        return userList && userList.length > 0;
    };

    private needHandleMouseWheelByCurrentState = () => {
        if (this.checkIsInAnyEditingState()) {
            return false;
        }

        if (!(this.canChangeSelectedSlide()) || this.menuManager?.isDropdownShown) {
            return false;
        }
        return true;
    };

    private needHandleMouseWheelByTimestamp = (e) => {
        const isMagicMouse = e.deltaY === 1 || e.deltaY === -1;

        const magicMouseRes = isMagicMouse ? this.isMagicMouseAction(e) : this.isNotMagicMouseAction(e);
        if (magicMouseRes !== undefined) return magicMouseRes;
        return true;
    };

    private handleMouseWheel = (e) => {
        if (this.mouseWheelTimer !== null) {
            clearTimeout(this.mouseWheelTimer);
        }
        // eslint-disable-next-line complexity
        this.mouseWheelTimer = setTimeout(() => {
            this.lastHandledTimestamp = e.timeStamp;
            if (!this.selectionService) {
                return;
            }
            if (e.deltaY < 0) {
                if (this.canChangeSelectedSlide()) {
                    this.selectionService?.selectedSlides.clear();
                    if (this.selectionService?.currentSlideIndex >= 0
                        && this.selectionService?.currentSlideIndex - 1 >= 0) {
                        this.editingStateManager && (this.editingStateManager.keyFlag4Moving = 37);
                        this.selectionService
                            ?.changeCurrentSlideIndexByUserAction(this.selectionService.currentSlideIndex - 1);
                        e.preventDefault();
                        e.stopPropagation();
                    } else {
                        this.selectionService.needClearSelectedSlideClearanceFlag += 1;
                    }
                }
            } else if (this.canChangeSelectedSlide()) {
                this.selectionService?.selectedSlides.clear();
                if (this.selectionService?.currentSlideIndex >= 0
                        && this.selectionService?.currentSlideIndex + 1
                            < this.selectionService?.slides.length) {
                    this.editingStateManager && (this.editingStateManager.keyFlag4Moving = 39);
                    this.selectionService
                        ?.changeCurrentSlideIndexByUserAction(this.selectionService.currentSlideIndex + 1);
                    e.preventDefault();
                    e.stopPropagation();
                } else {
                    this.selectionService.needClearSelectedSlideClearanceFlag += 1;
                }
            }
        }, 10);
    };

    // 忽略滚轮的场景
    private ignoreWheel = (canvasObj, e) => {
        // 轻微的滑动忽略切换响应，防止误触（这里取3仅为经验值）
        const ignoreMoveDistance = 3;
        const { className } = canvasObj;
        // 纵向出现滚动条，忽略切换
        if (className.indexOf('hasOverScrollY') >= 0) {
            return true;
        }
        // 主要做横向滑动，忽略切换
        if (
            e.deltaY === 0
            || (Math.abs(e.deltaX) >= Math.abs(e.deltaY))
            || (Math.abs(e.deltaY) < ignoreMoveDistance)
        ) {
            return true;
        }
    };

    private isMagicMouseAction(e) {
        if (Math.abs(e.deltaY) < 32) return false;   // /灵敏度处理

        // console.log('~_~', e.deltaY);
        if (e.timeStamp < this.outerEditorTimeStamp + 2000) {  // 先在editor外边滚，然后鼠标移动进来2s内不处理
            return false;
        }
        // /magic mouse 因为有回弹回掉需要持续1-1.5s 所以只能两秒之内只响应一次鼠标滚轮事件
        if (e.timeStamp > this.lastTimeStamp + 2000) {
            this.lastTimeStamp = e.timeStamp;
        } else if (e.timeStamp > this.lastHandledTimestamp + 500) {
            if (Math.abs(e.deltaY) > this.lastDeltaY && Math.abs(e.deltaY) > 32) {
                this.lastTimeStamp = e.timeStamp;
                this.lastDeltaY = e.deltaY;
            } else {
                this.lastDeltaY = e.deltaY;
                return false;
            }
        } else {
            return false;
        }
    }
    private isNotMagicMouseAction(e) {
        if (e.timeStamp < this.outerEditorTimeStamp + 500) {  // 先在editor外边滚，然后鼠标移动进来500s内不处理
            return false;
        }

        if (e.timeStamp > this.lastTimeStamp + 500) {   // /其它鼠标 所以只能500毫秒之内只响应一次鼠标滚轮事件
            this.lastTimeStamp = e.timeStamp;
        } else {
            return false;
        }
    }

    private deleteSlide() {
        if (!this.selectionService) {
            return;
        }
        if (this.selectionService.selectedSlides.selectedArray.length > 0) {
            this.uiDataService?.presentation?.deleteSlide();
            return;
        }
        if (this.selectionService?.currentSlide) {
            this.uiDataService?.presentation
                ?.deleteSlide([this.selectionService?.currentSlide]);
        }
    }

    private deleteInEditorRegion() {
        // 表格选中元素状态走表格的删除逻辑
        const selectedSlideElements = this.selectionService?.selectedElements
            .selectedArrayForType(SelectedEleMgrType.SLIDE);
        if (
            selectedSlideElements && selectedSlideElements.length === 1
            && selectedSlideElements[0].type === UIModelType.TABLE
        ) {
            const result = (selectedSlideElements[0] as ITableModel).clearSelectedCellContent();
            if (result === true) {
                return;
            }
        }
        this.selectionService?.currentSlide?.deleteElement();
    }
}

export type IBaseShortcutActions = Omit<BaseShortcutActions, 'setServicesDelegates'>;
